import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import { useAdminStore } from '../stores/useAdminStore.js';
import AdminCoursesTab from './admin/AdminCoursesTab.js';
import AdminUsersTab from './admin/AdminUsersTab.js';
import AdminStatsTab from './admin/AdminStatsTab.js';
import AdminPracticeMapTab from './admin/AdminPracticeMapTab.js';

type AdminTab = 'courses' | 'users' | 'stats' | 'map';

const TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'courses', label: 'Courses', icon: 'school' },
  { id: 'users', label: 'Users', icon: 'group' },
  { id: 'stats', label: 'Stats', icon: 'monitoring' },
  { id: 'map', label: 'Practice Map', icon: 'map' }
];

export default function Admin() {
  const { user } = useAuthStore();
  const { sessionUnlocked, setSessionUnlocked, clearSession } = useAdminStore();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<AdminTab>('courses');

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ allowed: boolean }>('/api/admin/access')
      .then(({ data }) => {
        if (!cancelled) setAllowed(data.allowed);
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => () => clearSession(), [clearSession]);

  if (allowed === null) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-surface-variant rounded w-1/3" />
        <div className="h-64 bg-surface-variant rounded" />
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/home" replace />;
  }

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/admin/unlock', { password });
      setSessionUnlocked(true);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Incorrect admin password'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!sessionUnlocked) {
    return (
      <div className="max-w-md mx-auto mt-16 p-8 bg-surface-container-low border border-outline-variant rounded-lg">
        <h1 className="text-headline-sm font-semibold text-on-surface mb-2">Admin access</h1>
        <p className="text-body-sm text-on-surface-variant mb-6">
          Signed in as <span className="text-on-surface font-medium">{user?.email}</span>. Enter the
          admin password to continue.
        </p>
        <form onSubmit={handleUnlock} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            autoComplete="off"
            className="w-full px-4 py-3 bg-surface-variant border border-outline-variant rounded text-on-surface focus:outline-none focus:border-error"
          />
          {error && <p className="text-error text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full bg-error text-white py-3 rounded font-button uppercase tracking-wider font-bold disabled:opacity-50 active:scale-[0.97]"
          >
            {loading ? 'Verifying…' : 'Unlock dashboard'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-headline-sm font-semibold text-on-surface">Admin dashboard</h1>
        <button
          type="button"
          onClick={() => clearSession()}
          className="text-sm text-on-surface-variant hover:text-error transition-colors"
        >
          Lock session
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-6 border-b border-outline-variant/40 pb-0">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-button transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-error text-on-surface'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'courses' && <AdminCoursesTab />}
      {tab === 'users' && <AdminUsersTab />}
      {tab === 'stats' && <AdminStatsTab />}
      {tab === 'map' && <AdminPracticeMapTab />}
    </div>
  );
}
